import { TemplateRef } from '@angular/core'
import { DateFormat, MomentConvertible } from '../../util'
import { BadgeColors } from '../badge'
import { Icon, IconAlignment, IconColor, IconGap, IconSize } from '../icon'
import { TooltipAlignment, TooltipDisplay, TooltipPosition } from '../tooltip'
import { TableAction } from './table-action'
import { CellData, CellType } from './table-cell.component'

export type SortOrder = 'asc' | 'desc'
export type TextTruncate = 'start' | 'end'
export type Alignment = 'start' | 'end'

export interface TableColumn<T> {
  name: string
  headerTitle: string
  sort?: SortOrder | undefined
  type: CellType
  alignment: Alignment
  getCell: (t: T) => CellData
  /**
   * Standard grid sizes here, 1 to 12 supported
   */
  gridSize: number
}

export interface TooltipDetails {
  message?: string
  position?: TooltipPosition
  alignment?: TooltipAlignment
  showDash?: boolean
  clickToStayOpen?: boolean
  forceDisplayState?: TooltipDisplay
}

export interface IconDetails {
  name: Icon
  size?: IconSize
  color?: IconColor
  alignment?: IconAlignment
  gapRight?: IconGap
  gapLeft?: IconGap
}

export interface BadgeDetails {
  label: string
  icon?: Icon
  color: BadgeColors
  greyTextColor?: boolean
}

export class TableColumnsBuilder<T> {
  columns: TableColumn<T>[] = []

  /**
   * Text Field with optional icon as a prefix
   */
  addText(
    name: string,
    gridSize: number,
    textFn: (t: T) => string | undefined,
    iconFn?: (t: T) => IconDetails | undefined,
    sort?: SortOrder,
    truncation?: TextTruncate,
    headerTitle?: string,
    canCopy: boolean = false,
    tooltipFn?: (t: T) => TooltipDetails | undefined,
  ): TableColumnsBuilder<T> {
    this.columns.push({
      name,
      type: 'text',
      alignment: 'start',
      headerTitle: headerTitle ?? name,
      sort,
      gridSize,
      getCell: (t: T) => ({
        data: textFn(t) ?? 'N/A',
        icon: iconFn ? iconFn(t) : undefined,
        truncation,
        canCopy,
        tooltip: tooltipFn ? tooltipFn(t) : undefined,
      }),
    })

    return this
  }

  addBadge(
    name: string,
    gridSize: number,
    badgeFn: (t: T) => BadgeDetails,
    sort?: SortOrder,
    truncation?: TextTruncate,
    headerTitle?: string,
  ): TableColumnsBuilder<T> {
    this.columns.push({
      name,
      type: 'badge',
      alignment: 'start',
      headerTitle: headerTitle ?? name,
      sort,
      gridSize,
      getCell: (t: T) => ({
        data: badgeFn(t),
        truncation,
      }),
    })

    return this
  }

  /**
   * Any Date/Time etc. uses ftr-date under the hood
   */
  addDate(
    name: string,
    gridSize: number,
    dateFn: (t: T) => MomentConvertible | undefined,
    format: DateFormat,
    iconFn?: (t: T) => IconDetails | undefined,
    sort?: SortOrder,
    truncation?: TextTruncate,
    titleFormat?: DateFormat,
    headerTitle?: string,
  ): TableColumnsBuilder<T> {
    this.columns.push({
      name,
      type: 'date',
      alignment: 'start',
      headerTitle: headerTitle ?? name,
      sort,
      gridSize,
      getCell: (t: T) => ({ data: dateFn(t), format, icon: iconFn ? iconFn(t) : undefined, truncation, titleFormat }),
    })
    return this
  }

  /**
   * Router Links
   */
  addLink(
    name: string,
    gridSize: number,
    textFn: (t: T) => string,
    routerLinkFn: (t: T) => string[],
    iconFn?: (t: T) => IconDetails | undefined,
    truncation?: TextTruncate,
    headerTitle?: string,
  ): TableColumnsBuilder<T> {
    this.columns.push({
      name,
      type: 'link',
      headerTitle: headerTitle ?? name,
      gridSize,
      alignment: 'start',
      getCell: (t: T) => ({
        data: textFn(t),
        routerLink: routerLinkFn(t),
        icon: iconFn ? iconFn(t) : undefined,
        truncation,
      }),
    })
    return this
  }

  /**
   * Adds action buttons
   * @param name
   * @param gridSize
   * @param actionsFn
   */
  addActions(name: string, gridSize: number, actionsFn: (t: T) => TableAction<T>[]): this {
    this.columns.push({
      name,
      type: 'action',
      headerTitle: name,
      gridSize,
      alignment: 'end',
      getCell: (t: T) => ({
        data: t,
        actions: actionsFn(t),
      }),
    })

    return this
  }

  addTemplate(name: string, gridSize: number, template: TemplateRef<any>, alignment: Alignment = 'start'): this {
    this.columns.push({
      name,
      type: 'template',
      headerTitle: name,
      gridSize,
      alignment,
      getCell: (t: T) => ({
        data: t,
        template,
      }),
    })

    return this
  }

  build(): TableColumn<T>[] {
    const total = this.columns.reduce((acc, c) => acc + c.gridSize, 0)
    if (total !== 12) {
      throw new Error('Table grids must add up to 12')
    }
    return [...this.columns]
  }
}
